<script setup lang="ts">
import {
  JoinShopRejectReason,
  pickShopCustomerIcon,
  SHOP_CUSTOMER_ICON_POOL,
  ShopWsClientEvent,
  ShopWsInboundEvent,
  parseShopWsInboundMessage,
  useUxFeedbackStore,
  WebsocketService,
  useUserStore,
} from '@blueprint/frontend-basics'
import type { ShopCustomerIcon } from '@blueprint/frontend-basics'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { ShopDto } from '@/models/dtos/shop.dto'
import { backendURL, wsDisableCustomerTtl } from '@/helpers/config.helper'
import { useServiceStore } from '@/stores/service.store'

const { t } = useI18n()
const serviceStore = useServiceStore()
const userStore = useUserStore()

const shops = ref<ShopDto[]>([])
const page = ref(0)
const pageSize = 10
const hasMore = ref(true)
const listLoading = ref(false)
const joinedShopId = ref<string | null>(null)
const joinInProgress = ref(false)

const customerWsService = ref<WebsocketService | null>(null)
const pendingJoinShopId = ref<string | null>(null)
let customerWsGeneration = 0

const ensureAnonymousSession = async (): Promise<void> => {
  if (!userStore.getToken) {
    await userStore.createAnonymousUser(serviceStore)
  }
}

const loadShops = async (append: boolean): Promise<void> => {
  if (listLoading.value || (!append && shops.value.length > 0 && page.value > 0)) {
    return
  }
  if (!hasMore.value && append) {
    return
  }

  listLoading.value = true
  try {
    await ensureAnonymousSession()
    const nextPage = append ? page.value + 1 : 0
    const result = await serviceStore.shopService.listShops(nextPage, pageSize)
    if (append) {
      shops.value.push(...result.items)
    } else {
      shops.value = result.items
    }
    page.value = result.page ?? nextPage
    hasMore.value = shops.value.length < (result.total ?? result.totalFiltered ?? 0)
  } catch {
    useUxFeedbackStore().queueMessage({
      type: 'error',
      title: t('general.error'),
      message: t('home.loadShopsError'),
    })
  } finally {
    listLoading.value = false
  }
}

const onInfiniteLoad = async ({
  done,
}: {
  done: (status: 'ok' | 'empty' | 'error') => void
}): Promise<void> => {
  if (!hasMore.value) {
    done('empty')
    return
  }
  const before = shops.value.length
  await loadShops(true)
  if (shops.value.length === before) {
    done('empty')
  } else if (!hasMore.value) {
    done('empty')
  } else {
    done('ok')
  }
}

/** Server closes customer sockets after ~45s; stop auto-reconnect and sync UI. */
const clearJoinedShopState = (): void => {
  joinedShopId.value = null
  joinInProgress.value = false
  pendingJoinShopId.value = null
}

const disconnectCustomerWebSocket = (): void => {
  const svc = customerWsService.value
  if (!svc) {
    return
  }
  svc.onReady = null
  svc.onOpen = null
  svc.onClose = null
  svc.onError = null
  svc.onMessage = null
  svc.onPermanentDisconnect = null
  svc.onReconnectScheduled = null
  svc.disconnect()
  customerWsService.value = null
}

const handleCustomerWebSocketClosed = (
  ws: WebsocketService,
  generation: number,
): void => {
  if (generation !== customerWsGeneration) {
    return
  }
  clearJoinedShopState()
  if (customerWsService.value === ws) {
    disconnectCustomerWebSocket()
  }
}

const leaveJoinedShop = (): void => {
  customerWsGeneration++
  clearJoinedShopState()
  disconnectCustomerWebSocket()
}

const MAX_JOIN_SHOP_ATTEMPTS = 5

const failJoinShop = (): void => {
  useUxFeedbackStore().queueMessage({
    type: 'error',
    title: t('general.error'),
    message: t('home.joinShopError'),
  })
  clearJoinedShopState()
}

const connectAndJoinShop = (shopId: string): void => {
  const generation = ++customerWsGeneration
  pendingJoinShopId.value = shopId
  joinedShopId.value = null
  disconnectCustomerWebSocket()

  const ws = new WebsocketService(backendURL, {
    role: 'customer',
    disableCustomerTtl: wsDisableCustomerTtl,
  })
  customerWsService.value = ws

  const excludedIcons = new Set<ShopCustomerIcon>()
  let joinAttempt = 0

  const tryJoinShop = (): void => {
    if (generation !== customerWsGeneration) {
      return
    }
    const targetId = pendingJoinShopId.value
    if (!targetId) {
      return
    }
    if (joinAttempt >= MAX_JOIN_SHOP_ATTEMPTS) {
      failJoinShop()
      return
    }
    joinAttempt += 1
    const icon = pickShopCustomerIcon(excludedIcons)
    ws.sendShopMessage(ShopWsClientEvent.joinShop, { shopId: targetId, icon })
  }

  ws.onReady = () => {
    if (generation !== customerWsGeneration) {
      return
    }
    tryJoinShop()
  }

  ws.onMessage = (raw) => {
    if (generation !== customerWsGeneration) {
      return
    }
    const message = parseShopWsInboundMessage(raw)
    if (!message) {
      return
    }
    if (message.event === ShopWsInboundEvent.shopJoined) {
      joinedShopId.value = pendingJoinShopId.value
      joinInProgress.value = false
      pendingJoinShopId.value = null
      return
    }
    if (message.event === ShopWsInboundEvent.joinShopRejected) {
      if (message.data.reason === JoinShopRejectReason.iconTaken) {
        const taken = message.data.icon
        if (
          taken &&
          (SHOP_CUSTOMER_ICON_POOL as readonly string[]).includes(taken)
        ) {
          excludedIcons.add(taken as ShopCustomerIcon)
        }
        tryJoinShop()
        return
      }
      failJoinShop()
    }
  }

  ws.onClose = () => {
    handleCustomerWebSocketClosed(ws, generation)
  }

  ws.onPermanentDisconnect = () => {
    if (generation !== customerWsGeneration) {
      return
    }
    clearJoinedShopState()
  }

  ws.connect(async () => {
    if (serviceStore.authService.isAuthenticated()) {
      await serviceStore.authService.refreshToken()
    }
    return userStore.getToken
  })
}

const onShopClick = async (shop: ShopDto): Promise<void> => {
  if (!shop.id || joinInProgress.value) {
    return
  }
  if (joinedShopId.value === shop.id) {
    leaveJoinedShop()
    return
  }

  joinInProgress.value = true
  await ensureAnonymousSession()
  connectAndJoinShop(shop.id)
}

onMounted(async () => {
  await ensureAnonymousSession()
  await loadShops(false)
})

onBeforeUnmount(() => {
  disconnectCustomerWebSocket()
})
</script>

<template>
  <v-container class="py-6" max-width="640">
    <h1 class="text-h5 mb-4">{{ t('home.shopsTitle') }}</h1>

    <v-progress-linear
      v-if="listLoading && shops.length === 0"
      indeterminate
      color="primary"
      class="mb-4"
    />

    <p v-else-if="shops.length === 0 && !listLoading" class="text-medium-emphasis">
      {{ t('home.noShops') }}
    </p>

    <v-infinite-scroll v-else :disabled="!hasMore" @load="onInfiniteLoad">
      <v-row dense>
        <v-col v-for="shop in shops" :key="shop.id" cols="12" sm="6">
          <v-card
            :variant="joinedShopId === shop.id ? 'elevated' : 'outlined'"
            :color="joinedShopId === shop.id ? 'primary' : undefined"
            :class="{ 'text-white': joinedShopId === shop.id }"
            class="shop-card"
            @click="onShopClick(shop)"
          >
            <v-card-title class="d-flex align-center justify-space-between">
              <span>{{ shop.name }}</span>
              <v-icon
                v-if="joinedShopId === shop.id"
                icon="mdi-check-circle"
                color="white"
              />
            </v-card-title>
            <v-card-subtitle
              :class="joinedShopId === shop.id ? 'text-white' : ''"
            >
              {{
                joinedShopId === shop.id
                  ? t('home.tapToLeave')
                  : t('home.tapToJoin')
              }}
            </v-card-subtitle>
          </v-card>
        </v-col>
      </v-row>
    </v-infinite-scroll>
  </v-container>
</template>

<style scoped lang="scss">
.shop-card {
  cursor: pointer;
  transition: box-shadow 0.2s ease;
}
</style>
